// components/plScenario/PLScenarioTable.jsx - REFACTORED
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { AdjustmentPropagationModal } from './AdjustmentPropagationModal';
import { ManageHiddenItemsModal } from './ManageHiddenItemsModal';
import PLLineItemDetailsModal from './PLLineItemDetailsModal';
import EmployeeSalaryModal from './EmployeeSalaryModal'; // ✅ ADD THIS

import { usePLLineItemDetails } from '../../../../hooks/playground/usePLLineItemDetails';

// Custom Hooks
import { usePLCalculations } from '../../../../hooks/playground/usePLCalculations';
import { useAccountSearch } from '../../../../hooks/playground/useAccountSearch';
import { useTemporaryRows } from '../../../../hooks/playground/useTemporaryRows';
import { useSectionCollapse } from '../../../../hooks/playground/useSectionCollapse';

// UI Components
import TableToolbar from './Table/TableToolbar';
import TableHeader from './Table/TableHeader';
import TotalRow from './Table/TotalRow';
import CategorySection from './Table/CategorySection';
import SearchModal from './Table/SearchModal';

import AMIBreakdownSection from './AMIBreakdownSection';

// Utils
import {
  formatCurrency,
  formatPercentage,
  hasAllZeroValues,
  groupBySubcategory,
  organizeLineItems,
  generateQuarters,
} from '../../../../utils/plScenarioUtils';

const PLScenarioTable = ({ 
  lineItems, 
  months, 
  currentMonth,
  selectedYear,
  onYearChange,
  currentYear,
  availableYears,
  totals,
  onUpdateAdjustment,
  onApplyToAllFuture,
  userAdjustments,
  onResetCallback,
  onLoadTemporaryRowsCallback,
  onResetAdjustments,
  hasAdjustments = false,
  onTemporaryRowsChange,  // ✅ ADD THIS

  // Visibility props
  hiddenItems = [],
  isVisibilityEditMode = false,
  showHiddenItems = false,
  isSavingVisibility = false,
  hiddenCount = 0,
  hasUnsavedVisibilityChanges = false,
  onToggleItemVisibility,
  onIsItemHidden,
  onStartVisibilityEditing,
  onSaveVisibilityChanges,
  onCancelVisibilityEditing,
  onToggleShowHidden,
  onRestoreItem,
}) => {
  // View state
  const [viewMode, setViewMode] = useState('monthly');
  const [hideZeroRows, setHideZeroRows] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  
  // Editing state
  const [editingCell, setEditingCell] = useState(null);
  const [pendingAdjustment, setPendingAdjustment] = useState(null);
  
  // Details modal state
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEmployeeSalaryModal, setShowEmployeeSalaryModal] = useState(false); // ✅ ADD THIS

  const rowRefs = useRef({});

  // Organize data
  const organizedData = useMemo(() => organizeLineItems(lineItems), [lineItems]);

  // Generate quarters
  const quarters = useMemo(() => 
    viewMode === 'quarterly' ? generateQuarters(months) : [], 
    [months, viewMode]
  );

  // Custom hooks
  const {
    temporaryRows,
    editingTempName,
    setEditingTempName,
    addTemporaryRow,
    deleteTemporaryRow,
    updateTemporaryRowName,
    updateTemporaryRowValue,
    resetTemporaryRows,
    loadTemporaryRows,
  } = useTemporaryRows(months);

  useEffect(() => {
    // If parent needs access to temporaryRows, provide it via a callback prop
    if (onTemporaryRowsChange) {
      onTemporaryRowsChange(temporaryRows);
    }
  }, [temporaryRows]);

  const {
    collapsedSections,
    setCollapsedSections,
    toggleSection,
    isCollapsed,
    getDetailLevel,
  } = useSectionCollapse(organizedData, lineItems, groupBySubcategory);

const {
  searchTerm,
  setSearchTerm,
  isSearchOpen,
  setIsSearchOpen,
  searchResults,
  currentSearchIndex,
  highlightedRow,
  setHighlightedRow,
  handleNextResult,
  handlePrevResult,
  handleSelectResult,
  clearSearch,
  clearSearchAfterDelay, // ADD THIS LINE
} = useAccountSearch(lineItems);

  const calculations = usePLCalculations(organizedData, temporaryRows, months);

  // Line item details hook
  const {
    account: accountInfo,
    monthlyTotals,
    transactions,
    loading: isLoadingDetails,
    error: detailsError,
    initialized
  } = usePLLineItemDetails(
    selectedAccountId,
    selectedYear,
    'Accrual',
    !!selectedAccountId
  );

  // Register reset function
  const resetFunctionRef = useRef(() => {
    resetTemporaryRows();
  });

  const loadTemporaryRowsRef = useRef(loadTemporaryRows);
  loadTemporaryRowsRef.current = loadTemporaryRows;


  useEffect(() => {
    if (onResetCallback) {
      onResetCallback(resetFunctionRef.current);
    }
    if (onLoadTemporaryRowsCallback) {
      onLoadTemporaryRowsCallback(loadTemporaryRowsRef.current);
    }
  }, [onResetCallback, onLoadTemporaryRowsCallback]);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setIsSearchOpen]);

  // Expand section for search result
  const expandSectionForItem = useCallback((item) => {
    const classification = item.classification;
    const accountType = item.account_type;
    
    let mainSection = '';
    let sectionPrefix = '';
    
    if (classification === 'Revenue') {
      if (accountType === 'Income') {
        mainSection = 'main-income';
        sectionPrefix = 'income';
      } else {
        mainSection = 'main-other-income';
        sectionPrefix = 'other-income';
      }
    } else if (classification === 'Expense') {
      if (accountType === 'Cost of Goods Sold') {
        mainSection = 'main-cogs';
        sectionPrefix = 'cogs';
      } else if (accountType === 'Other Expense') {
        mainSection = 'main-other-expenses';
        sectionPrefix = 'other-expenses';
      } else {
        mainSection = 'main-expenses';
        sectionPrefix = 'expense';
      }
    }
    
    if (mainSection) {
      const newSet = new Set(collapsedSections);
      newSet.delete(mainSection);
      
      const fqn = item.fully_qualified_name || item.account_name;
      const parts = fqn.split(':');
      const subcategory = parts.length > 1 ? parts[0] : 'Other';
      const subcategoryKey = `${sectionPrefix}-${subcategory}`;
      newSet.delete(subcategoryKey);
      
      setCollapsedSections(newSet);
    }
  }, [collapsedSections, setCollapsedSections]);

  // Scroll to search result
  useEffect(() => {
    if (currentSearchIndex >= 0 && searchResults.length > 0) {
      const currentResult = searchResults[currentSearchIndex];
      expandSectionForItem(currentResult);
      
      setTimeout(() => {
        const rowRef = rowRefs.current[currentResult.account_id];
        if (rowRef) {
          setHighlightedRow(currentResult.account_id);
          rowRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => setHighlightedRow(null), 3000);
        }
      }, 100);
    }
  }, [currentSearchIndex, searchResults, expandSectionForItem, setHighlightedRow]);

  // Display option change handler
  const handleDisplayOptionChange = useCallback((valueOrEvent) => {
    const value = typeof valueOrEvent === 'string' 
      ? valueOrEvent 
      : valueOrEvent.target.value;
    
    const [newViewMode, detailLevel] = value.split('-');
    
    if (newViewMode !== viewMode) {
      setViewMode(newViewMode);
    }
    
    if (detailLevel === 'expanded') {
      setCollapsedSections(new Set());
    } else if (detailLevel === 'categories') {
      const subcategories = new Set();
      
      const allGroups = [
        ...Object.keys(groupBySubcategory(organizedData.income)).map(s => `income-${s}`),
        ...Object.keys(groupBySubcategory(organizedData.cogs)).map(s => `cogs-${s}`),
        ...Object.keys(groupBySubcategory(organizedData.expenses)).map(s => `expense-${s}`),
        ...Object.keys(groupBySubcategory(organizedData.otherIncome)).map(s => `other-income-${s}`),
        ...Object.keys(groupBySubcategory(organizedData.otherExpenses)).map(s => `other-expenses-${s}`),
      ];
      
      allGroups.forEach(key => subcategories.add(key));
      setCollapsedSections(subcategories);
    } else if (detailLevel === 'sections') {
      setCollapsedSections(new Set([
        'main-income',
        'main-cogs',
        'main-expenses',
        'main-other-income',
        'main-other-expenses',
        'ami-breakdown',
      ]));
    }
  }, [viewMode, organizedData, setCollapsedSections]);

  // Cell editing handlers
  const handleCellEdit = (accountId, monthNum, currentValue) => {
    const roundedValue = Math.round(currentValue);
    setEditingCell({ accountId, monthNum, value: roundedValue, originalValue: currentValue });
  };

  const handleCellSave = () => {
    if (editingCell) {
      const value = parseFloat(editingCell.value) || 0;
      if (value !== editingCell.originalValue) {
        // Get current projected values for all months
        const item = lineItems.find(i => i.account_id === editingCell.accountId);
        const currentValues = item ? item.projected_amounts : {};
        
        setPendingAdjustment({
          accountId: editingCell.accountId,
          monthNum: editingCell.monthNum,
          value: value,
          year: selectedYear,
          currentValues: currentValues, // Add this
        });
      }
      setEditingCell(null);
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
  };

  const handleCellChange = (value) => {
    setEditingCell(prev => ({ ...prev, value }));
  };

  const handlePropagationChoice = (choice, bulkValues = null) => {
    if (!pendingAdjustment) return;

    const { accountId, monthNum, value, year } = pendingAdjustment;

    if (choice === 'bulk' && bulkValues) {
      // Apply bulk values for all months
      Object.entries(bulkValues).forEach(([month, val]) => {
        onUpdateAdjustment(accountId, parseInt(month), val, year);
      });
    } else if (choice === 'single') {
      onUpdateAdjustment(accountId, monthNum, value, year);
    } else if (choice === 'future-months') {
      onApplyToAllFuture(accountId, monthNum, value, year, 'same-year');
    } else if (choice === 'future-years') {
      onApplyToAllFuture(accountId, monthNum, value, year, 'all-years');
    }

    setPendingAdjustment(null);
  };

  // Add this new function after the expandSectionForItem function (around line 180)
  const navigateToSearchResult = useCallback((index) => {
    if (index >= 0 && searchResults.length > 0) {
      const currentResult = searchResults[index];
      expandSectionForItem(currentResult);
      
      setTimeout(() => {
        const rowRef = rowRefs.current[currentResult.account_id];
        if (rowRef) {
          setHighlightedRow(currentResult.account_id);
          rowRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => setHighlightedRow(null), 3000);
        }
      }, 100);
    }
  }, [searchResults, expandSectionForItem, setHighlightedRow]);

  // Update the existing useEffect (around line 210) to use this function
  useEffect(() => {
    if (currentSearchIndex >= 0 && searchResults.length > 0) {
      navigateToSearchResult(currentSearchIndex);
    }
  }, [currentSearchIndex, navigateToSearchResult]);

  // Line item details handlers
  const handleLineItemClick = (accountId) => {
    console.log('🔍 Clicked account:', accountId, typeof accountId);
    
    // Check if this is the Wages and Salary account
    if (String(accountId) === '350') {
      console.log('💰 Opening Employee Salary Modal');
      setShowEmployeeSalaryModal(true);
      return;
    }
    
    console.log('📊 Opening Line Item Details Modal');
    setSelectedAccountId(String(accountId));
    setShowDetailsModal(true);
};

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setTimeout(() => setSelectedAccountId(null), 300);
  };

  // Visibility filter
  const shouldShowItem = useCallback((item) => {
    const isHidden = onIsItemHidden(item.account_id);
    if (showHiddenItems) return true;
    return !isHidden;
  }, [onIsItemHidden, showHiddenItems]);

  // Check if edited
  const isEdited = (accountId, monthNum) => {
    const key = `${selectedYear}-${accountId}-${monthNum}`;
    const hasAdjustment = userAdjustments[key] !== undefined;
    
    // Only show as edited if:
    // 1. Adjustment exists, AND
    // 2. Month is in the future (or we're viewing a future year)
    if (!hasAdjustment) return false;
    
    // For current year, only future months can be adjusted
    if (selectedYear === currentYear) {
      return monthNum > currentMonth;
    }
    
    // For future years, all months can be adjusted
    return selectedYear > currentYear;
  };

  // Create a function to set row refs
  const setRowRef = useCallback((accountId, element) => {
    if (element) {
      rowRefs.current[accountId] = element;
    } else {
      delete rowRefs.current[accountId];
    }
  }, []);

  // Prepare props for child components
  const lineItemProps = {
    editingCell,
    userAdjustments,
    selectedYear,
    currentYear,
    currentMonth,
    isVisibilityEditMode,
    showHiddenItems,
    highlightedRow,
    onCellEdit: handleCellEdit,
    onCellSave: handleCellSave,
    onCellCancel: handleCellCancel,
    onCellChange: handleCellChange,
    onToggleItemVisibility,
    onIsItemHidden,
    onLineItemClick: handleLineItemClick,
    formatCurrency,
    setRowRef, // CHANGED: from rowRef to setRowRef
  };

  const tempRowProps = {
    editingCell,
    editingTempName,
    currentYear,      // ✅ ADD THIS
    currentMonth,     // ✅ ADD THIS
    selectedYear,     // ✅ ADD THIS
    onNameEdit: setEditingTempName,
    onNameChange: updateTemporaryRowName,
    onNameBlur: setEditingTempName,
    onCellEdit: handleCellEdit,
    onCellChange: handleCellChange,
    onCellBlur: (groupKey, rowId, monthNum, value) => {
      if (groupKey && rowId && monthNum && value !== undefined) {
        const parsedValue = parseFloat(value) || 0;
        updateTemporaryRowValue(groupKey, rowId, monthNum, parsedValue);
      }
      setEditingCell(null);
    },
    onDelete: deleteTemporaryRow,
    formatCurrency,
  };

  const columnCount = viewMode === 'monthly' ? 14 : 6;

  return (
    <div className="relative">
      {/* Modals */}
      {pendingAdjustment && (
        <AdjustmentPropagationModal
          adjustment={pendingAdjustment}
          onChoice={handlePropagationChoice}
          onCancel={() => setPendingAdjustment(null)}
          months={months}
          selectedYear={selectedYear}
          availableYears={availableYears}
        />
      )}

      <SearchModal
        isOpen={isSearchOpen}
        searchTerm={searchTerm}
        searchResults={searchResults}
        currentSearchIndex={currentSearchIndex}
        onSearchChange={setSearchTerm}
        onClose={clearSearch}
        onNext={handleNextResult}
        onPrev={handlePrevResult}
        onSelectResult={handleSelectResult}
        onClearAfterDelay={clearSearchAfterDelay}
        onNavigateToResult={navigateToSearchResult} // ADD THIS
      />

      <ManageHiddenItemsModal
        isOpen={showManageModal}
        onClose={() => setShowManageModal(false)}
        hiddenItems={hiddenItems}
        lineItems={lineItems}
        onRestoreItem={onRestoreItem}
      />

      <PLLineItemDetailsModal
        accountInfo={accountInfo}
        monthlyTotals={monthlyTotals}
        transactions={transactions}
        metadata={{}}
        isOpen={showDetailsModal && !!selectedAccountId}
        onClose={handleCloseDetailsModal}
      />

      {/* ✅ ADD THIS - Employee Salary Modal */}
      <EmployeeSalaryModal
        isOpen={showEmployeeSalaryModal}
        onClose={() => setShowEmployeeSalaryModal(false)}
        year={selectedYear}
      />

      {/* Toolbar */}
      <TableToolbar
        onOpenSearch={() => setIsSearchOpen(true)}
        viewMode={viewMode}
        detailLevel={getDetailLevel()}
        onViewModeChange={handleDisplayOptionChange}
        hideZeroRows={hideZeroRows}
        onToggleHideZeroRows={() => setHideZeroRows(!hideZeroRows)}
        hiddenCount={hiddenCount}
        isVisibilityEditMode={isVisibilityEditMode}
        showHiddenItems={showHiddenItems}
        hasUnsavedVisibilityChanges={hasUnsavedVisibilityChanges}
        isSavingVisibility={isSavingVisibility}
        onStartVisibilityEditing={onStartVisibilityEditing}
        onSaveVisibilityChanges={onSaveVisibilityChanges}
        onCancelVisibilityEditing={onCancelVisibilityEditing}
        onToggleShowHidden={onToggleShowHidden}
        onManageHidden={() => setShowManageModal(true)}
        hasAdjustments={hasAdjustments}
        adjustmentsCount={Object.keys(userAdjustments).length}
        onResetAdjustments={onResetAdjustments}
      />

      {/* Table */}
      <div className="overflow-auto max-h-[calc(100vh-200px)] border border-gray-200 rounded-lg">
        <table className="min-w-full border-collapse">
          <TableHeader
            viewMode={viewMode}
            months={months}
            quarters={quarters}
            selectedYear={selectedYear}
          />
          <tbody>
            {/* INCOME SECTION */}
            <TotalRow
              label="Total Income"
              monthlyTotals={calculations.incomeTotals}
              months={months}
              quarters={quarters}
              viewMode={viewMode}
              collapsible={true}
              sectionKey="main-income"
              isCollapsed={isCollapsed('main-income')}
              onToggle={() => toggleSection('main-income')}
              formatCurrency={formatCurrency}
            />
            {!isCollapsed('main-income') && 
              Object.entries(groupBySubcategory(organizedData.income)).map(([subcategory, items]) => (
                <CategorySection
                  key={`income-${subcategory}`}
                  title={subcategory}
                  items={items.filter(shouldShowItem).filter(item => !hideZeroRows || !hasAllZeroValues(item))}
                  sectionKey={`income-${subcategory}`}
                  mainSection="income"
                  viewMode={viewMode}
                  months={months}
                  quarters={quarters}
                  isCollapsed={isCollapsed}
                  onToggleSection={toggleSection}
                  calculateGroupTotal={calculations.calculateGroupTotal}
                  calculateQuarterlyTotal={calculations.calculateQuarterlyTotal}
                  temporaryRows={temporaryRows}
                  onAddTemporaryRow={addTemporaryRow}
                  formatCurrency={formatCurrency}
                  {...lineItemProps}
                  {...tempRowProps}
                />
              ))
            }

            {/* COGS SECTION */}
            <tr className="bg-red-50">
              <td colSpan={columnCount} className="py-0.5"></td>
            </tr>
            <TotalRow
              label="Total Cost of Goods Sold"
              monthlyTotals={calculations.cogsTotals}
              months={months}
              quarters={quarters}
              viewMode={viewMode}
              collapsible={true}
              sectionKey="main-cogs"
              isCollapsed={isCollapsed('main-cogs')}
              onToggle={() => toggleSection('main-cogs')}
              formatCurrency={formatCurrency}
            />
            {!isCollapsed('main-cogs') &&
              Object.entries(groupBySubcategory(organizedData.cogs)).map(([subcategory, items]) => (
                <CategorySection
                  key={`cogs-${subcategory}`}
                  title={subcategory}
                  items={items.filter(shouldShowItem).filter(item => !hideZeroRows || !hasAllZeroValues(item))}
                  sectionKey={`cogs-${subcategory}`}
                  mainSection="cogs"
                  viewMode={viewMode}
                  months={months}
                  quarters={quarters}
                  isCollapsed={isCollapsed}
                  onToggleSection={toggleSection}
                  calculateGroupTotal={calculations.calculateGroupTotal}
                  calculateQuarterlyTotal={calculations.calculateQuarterlyTotal}
                  temporaryRows={temporaryRows}
                  onAddTemporaryRow={addTemporaryRow}
                  formatCurrency={formatCurrency}
                  {...lineItemProps}
                  {...tempRowProps}
                />
              ))
            }

            {/* GROSS PROFIT */}
            <TotalRow
              label="Gross Profit"
              monthlyTotals={calculations.grossProfitTotals}
              months={months}
              quarters={quarters}
              viewMode={viewMode}
              formatCurrency={formatCurrency}
            />

            {/* EXPENSES SECTION */}
            <tr className="bg-orange-50">
              <td colSpan={columnCount} className="py-0.5"></td>
            </tr>
            <TotalRow
              label="Total Expenses"
              monthlyTotals={calculations.expensesTotals}
              months={months}
              quarters={quarters}
              viewMode={viewMode}
              collapsible={true}
              sectionKey="main-expenses"
              isCollapsed={isCollapsed('main-expenses')}
              onToggle={() => toggleSection('main-expenses')}
              formatCurrency={formatCurrency}
            />
            {!isCollapsed('main-expenses') &&
              Object.entries(groupBySubcategory(organizedData.expenses)).map(([subcategory, items]) => (
                <CategorySection
                  key={`expense-${subcategory}`}
                  title={subcategory}
                  items={items.filter(shouldShowItem).filter(item => !hideZeroRows || !hasAllZeroValues(item))}
                  sectionKey={`expense-${subcategory}`}
                  mainSection="expenses"
                  viewMode={viewMode}
                  months={months}
                  quarters={quarters}
                  isCollapsed={isCollapsed}
                  onToggleSection={toggleSection}
                  calculateGroupTotal={calculations.calculateGroupTotal}
                  calculateQuarterlyTotal={calculations.calculateQuarterlyTotal}
                  temporaryRows={temporaryRows}
                  onAddTemporaryRow={addTemporaryRow}
                  formatCurrency={formatCurrency}
                  {...lineItemProps}
                  {...tempRowProps}
                />
              ))
            }

            {/* NET OPERATING INCOME */}
            <TotalRow
              label="Net Operating Income"
              monthlyTotals={calculations.netOperatingIncomeTotals}
              months={months}
              quarters={quarters}
              viewMode={viewMode}
              formatCurrency={formatCurrency}
            />

            {/* OTHER INCOME/EXPENSES */}
            {organizedData.otherIncome.length > 0 && (
              <>
                <tr className="bg-green-100">
                  <td colSpan={columnCount} className="py-0.5"></td>
                </tr>
                <TotalRow
                  label="Total Other Income"
                  monthlyTotals={calculations.otherIncomeTotals}
                  months={months}
                  quarters={quarters}
                  viewMode={viewMode}
                  collapsible={true}
                  sectionKey="main-other-income"
                  isCollapsed={isCollapsed('main-other-income')}
                  onToggle={() => toggleSection('main-other-income')}
                  formatCurrency={formatCurrency}
                />
                {!isCollapsed('main-other-income') &&
                  Object.entries(groupBySubcategory(organizedData.otherIncome)).map(([subcategory, items]) => (
                    <CategorySection
                      key={`other-income-${subcategory}`}
                      title={subcategory}
                      items={items.filter(shouldShowItem).filter(item => !hideZeroRows || !hasAllZeroValues(item))}
                      sectionKey={`other-income-${subcategory}`}
                      mainSection="other-income"
                      viewMode={viewMode}
                      months={months}
                      quarters={quarters}
                      isCollapsed={isCollapsed}
                      onToggleSection={toggleSection}
                      calculateGroupTotal={calculations.calculateGroupTotal}
                      calculateQuarterlyTotal={calculations.calculateQuarterlyTotal}
                      temporaryRows={temporaryRows}
                      onAddTemporaryRow={addTemporaryRow}
                      formatCurrency={formatCurrency}
                      {...lineItemProps}
                      {...tempRowProps}
                    />
                  ))
                }
              </>
            )}

            {organizedData.otherExpenses.length > 0 && (
              <>
                <tr className="bg-red-50">
                  <td colSpan={columnCount} className="py-0.5"></td>
                </tr>
                <TotalRow
                  label="Total Other Expenses"
                  monthlyTotals={calculations.otherExpensesTotals}
                  months={months}
                  quarters={quarters}
                  viewMode={viewMode}
                  collapsible={true}
                  sectionKey="main-other-expenses"
                  isCollapsed={isCollapsed('main-other-expenses')}
                  onToggle={() => toggleSection('main-other-expenses')}
                  formatCurrency={formatCurrency}
                />
                {!isCollapsed('main-other-expenses') &&
                  Object.entries(groupBySubcategory(organizedData.otherExpenses)).map(([subcategory, items]) => (
                    <CategorySection
                      key={`other-expenses-${subcategory}`}
                      title={subcategory}
                      items={items.filter(shouldShowItem).filter(item => !hideZeroRows || !hasAllZeroValues(item))}
                      sectionKey={`other-expenses-${subcategory}`}
                      mainSection="other-expenses"
                      viewMode={viewMode}
                      months={months}
                      quarters={quarters}
                      isCollapsed={isCollapsed}
                      onToggleSection={toggleSection}
                      calculateGroupTotal={calculations.calculateGroupTotal}
                      calculateQuarterlyTotal={calculations.calculateQuarterlyTotal}
                      temporaryRows={temporaryRows}
                      onAddTemporaryRow={addTemporaryRow}
                      formatCurrency={formatCurrency}
                      {...lineItemProps}
                      {...tempRowProps}
                    />
                  ))
                }
              </>
            )}

            {/* NET OTHER INCOME */}
            {(organizedData.otherIncome.length > 0 || organizedData.otherExpenses.length > 0) && 
              <TotalRow
                label="Net Other Income"
                monthlyTotals={calculations.netOtherIncomeTotals}
                months={months}
                quarters={quarters}
                viewMode={viewMode}
                formatCurrency={formatCurrency}
              />
            }

            {/* NET INCOME */}
            <TotalRow
              label="NET INCOME"
              monthlyTotals={calculations.netIncomeTotals}
              months={months}
              quarters={quarters}
              viewMode={viewMode}
              bgColor="bg-darkblue-500"
              textColor="text-white"
              formatCurrency={formatCurrency}
            />

            {/* SPACING */}
            <tr>
              <td colSpan={columnCount} className="py-3"></td>
            </tr>

            {/* AMI BREAKDOWN */}
            <AMIBreakdownSection
              isCollapsed={isCollapsed('ami-breakdown')}
              onToggle={() => toggleSection('ami-breakdown')}
              amiBreakdown={calculations.amiBreakdown}
              months={months}
              quarters={quarters}
              viewMode={viewMode}
              formatPercentage={formatPercentage}
              formatCurrency={formatCurrency}
            />
          </tbody>
        </table>
      </div>

      {/* Loading overlay */}
      {isLoadingDetails && showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-sm text-gray-600">Loading transaction details...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PLScenarioTable;